import axios from "../axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUpdateUserCoins } from "@hooks/useUserInfos";
import {
  API_SHOP_PRODUCTS,
  API_SHOP_BUY,
  API_SHOP_CATEGORY,
} from "../constants/api";

export const PRODUCT_KEYS = {
  all: ["products", "avatars"],
  byUser: (userId) => ["products", userId],
  avatarsByUser: (userId) => ["avatars", userId],
};

// Funções puras de API (JavaScript padrão sem Hooks)
export async function fetchShopProducts(userId) {
  const { data } = await axios.get(API_SHOP_PRODUCTS(userId));
  return data;
}

export async function fnBuyItem({ productId, userSession }) {
  const { data } = await axios.post(API_SHOP_BUY, {
    reward_id: productId,
    user_sub: userSession?.sub,
  });

  return data;
}

export async function fnEarnItem({
  slug_reward,
  option_reward,
  userSession,
  token_reward,
  theme,
  value,
}) {
  const { data } = await axios.post(API_SHOP_BUY, {
    slug_reward,
    option_reward,
    user_sub: userSession?.sub,
    token_reward,
    theme_ticket: theme,
  });

  return data;
}

// Hook para buscar produtos
export function useProducts(userId) {
  return useQuery({
    queryKey: PRODUCT_KEYS.byUser(userId),
    queryFn: () => fetchShopProducts(userId),
    enabled: !!userId, // Só executa se o userId existir
    staleTime: 1000 * 60 * 60 * 3,
    gcTime: 1000 * 60 * 60 * 3,
    meta: { persist: true },
  });
}

// Hook para resgatar produto
export function useRedeemProduct() {
  const queryClient = useQueryClient();
  const updateUserCoins = useUpdateUserCoins();

  return useMutation({
    mutationFn: fnBuyItem,
    onSuccess: (data, variables) => {
      updateUserCoins(-variables.star_price);

      const userCacheKey = PRODUCT_KEYS.byUser(variables.userSession?.sub);
      queryClient.setQueryData(userCacheKey, (oldProducts) => {
        if (!oldProducts) return oldProducts;

        return oldProducts.map((product) => {
          if (product.id === variables.productId) {
            return {
              ...product,
              owned: true,
              cur_instalments:
                product.max_instalments == 1 ? 1 : product.cur_instalments + 1,
            };
          }
          return product;
        });
      });
    },
  });
}

// Hook para ganhar produto da troca de item usar principalmente em eventos
export function useEarnItem() {
  const queryClient = useQueryClient();
  const updateUserCoins = useUpdateUserCoins();

  return useMutation({
    mutationFn: fnEarnItem,
    onSuccess: (data, variables) => {
      const userId = variables.userSession?.sub;
      if (variables?.value) {
        updateUserCoins(variables.value);
      }
      if (userId) {
        queryClient.invalidateQueries({
          queryKey: PRODUCT_KEYS.byUser(userId),
        });
      }
    },
  });
}

// Função para buscar apenas produtos da categoria "Avatars"
async function getAvatarProducts(currentUser) {
  try {
    const response = await axios({
      url: API_SHOP_CATEGORY(currentUser.sub, "Avatars"),
      method: "GET",
    });

    return response.data;
  } catch (error) {
    throw error;
  }
}

// Busca os avatars usando o TanStack Query
export function useAvatarProducts(currentUser) {
  const { data } = useQuery({
    // Chave usada para armazenar os avatars desse usuário no cache
    queryKey: PRODUCT_KEYS.avatarsByUser(currentUser?.sub),

    // Função responsável por buscar os avatars na API
    queryFn: () => getAvatarProducts(currentUser),

    // Só faz a busca quando existir um usuário
    enabled: !!currentUser?.sub,
  });

  return { data };
}
